import { BillingService, ListPurchaseFilter, Purchase, PurchaseStatus } from '@galaxy/billing';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { getRequestPageSize } from '../shared/datasource-helpers';

@Injectable()
export class PurchaseDataSourceService extends DataSource<Purchase> {
  private storedPurchases$$: BehaviorSubject<Purchase[]>;
  private displayedPurchases$$: BehaviorSubject<Purchase[]>;
  private loading$$: BehaviorSubject<boolean>;
  private empty$$: BehaviorSubject<boolean>;

  private cursor$$: BehaviorSubject<string>;
  private hasMore$$: BehaviorSubject<boolean>;

  constructor(private billingService: BillingService) {
    super();
    this.storedPurchases$$ = new BehaviorSubject<Purchase[]>([]);
    this.displayedPurchases$$ = new BehaviorSubject<Purchase[]>([]);
    this.loading$$ = new BehaviorSubject<boolean>(true);
    this.empty$$ = new BehaviorSubject<boolean>(true);
    this.hasMore$$ = new BehaviorSubject<boolean>(false);
    this.cursor$$ = new BehaviorSubject<string>('');
  }

  get hasMore$(): Observable<boolean> {
    return this.hasMore$$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  get empty$(): Observable<boolean> {
    return this.empty$$.asObservable();
  }

  get storedPurchases$(): Observable<Purchase[]> {
    return this.storedPurchases$$.asObservable();
  }

  get displayedPurchases$(): Observable<Purchase[]> {
    return this.displayedPurchases$$.asObservable();
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<Purchase[]> {
    return this.displayedPurchases$$.asObservable();
  }

  disconnect(): void {
    this.storedPurchases$$.complete();
    this.displayedPurchases$$.complete();
    this.loading$$.complete();
    this.empty$$.complete();
    this.cursor$$.complete();
    this.hasMore$$.complete();
  }

  public updatePage(filters: ListPurchaseFilter, pageSize: number, pageIndex: number): void {
    const storedItems = this.storedPurchases$$.getValue();
    const requestPageSize = getRequestPageSize(storedItems.length, pageIndex, pageSize);
    if (this.hasMore$$.getValue() && requestPageSize > 0) {
      this.loadPurchases(filters, pageSize, pageIndex);
    } else {
      this.setPagedData(storedItems, pageIndex, pageSize);
    }
  }

  public loadPurchases(filters: ListPurchaseFilter, pageSize: number, pageIndex: number): void {
    this.listPurchases(filters, this.cursor$$.getValue(), pageSize)
      .pipe(map((newPurchases) => [...this.storedPurchases$$.value, ...newPurchases]))
      .subscribe((Purchases) => {
        this.storedPurchases$$.next(Purchases);
        this.setPagedData(Purchases, pageIndex, pageSize);
      });
  }

  public reloadPurchases(filters: ListPurchaseFilter, pageSize: number, pageIndex: number): void {
    this.listPurchases(filters, '', pageSize).subscribe((items) => {
      this.storedPurchases$$.next(items);
      this.setPagedData(items, pageIndex, pageSize);
    });
  }

  private setPagedData(Purchases: Purchase[], pageIndex: number, pageSize: number): void {
    // return the whole page, or whatever is left of our purchase list
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const endIndexForExisting = Math.min(Purchases.length, endIndex);

    const purchases = Purchases.slice(startIndex, endIndexForExisting).map((p) => {
      return {
        ...p,
        canPauseDunning: this.canPauseDunning(p),
        canResumeDunning: this.canResumeDunning(p),
      };
    });
    this.displayedPurchases$$.next(purchases);
  }

  private listPurchases(filters: ListPurchaseFilter, cursor: string, pageSize: number): Observable<Purchase[]> {
    this.loading$$.next(true);
    return this.billingService.listPurchases(filters, cursor, pageSize).pipe(
      tap((r) => {
        this.hasMore$$.next(r.hasMore);
        this.cursor$$.next(r.nextCursor);
        if ((r.results || []).length > 0) {
          this.empty$$.next(false);
        } else {
          this.empty$$.next(true);
        }
      }),
      map((r) => r.results),
      finalize(() => this.loading$$.next(false)),
    );
  }

  private canPauseDunning(purchase: Purchase): boolean {
    if (purchase.paid || purchase.voided) {
      return false;
    }

    const nonPausableStatuses = [PurchaseStatus.Unset, PurchaseStatus.Failed, PurchaseStatus.Success];
    if (nonPausableStatuses.includes(purchase.status)) {
      return false;
    }

    const now = new Date();
    if (!!purchase.pauseDunningUntil && purchase.pauseDunningUntil > now) {
      return false;
    }

    return true;
  }

  private canResumeDunning(purchase: Purchase): boolean {
    if (purchase.paid || purchase.voided) {
      return false;
    }

    const now = new Date();
    return !!purchase.pauseDunningUntil && purchase.pauseDunningUntil > now;
  }
}
